import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import { sportsData } from "../constants/constants";
import useAxiosConfig from "../config/AxiosConfig";

const CreateBookingModal = ({
  showModal,
  handleClose,
  handleSave,
  grounds,
  isLoadingGrounds,
}) => {
  const axiosInstance = useAxiosConfig();
  const [selectedGround, setSelectedGround] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSport, setSelectedSport] = useState("");
  const [selectedField, setSelectedField] = useState("");
  const [selectedSubfield, setSelectedSubfield] = useState("");
  const [duration, setDuration] = useState("60");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [bookedBy, setBookedBy] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [bookingAmount, setBookingAmount] = useState("");
  const [paymentType, setPaymentType] = useState("Advance");
  const [description, setDescription] = useState("");
  const [availableSports, setAvailableSports] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [availableSubfields, setAvailableSubfields] = useState([]);
  const [availableDurations, setAvailableDurations] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [, setIsLoadingPrice] = useState(false);

  // Reset form when modal closes
  useEffect(() => {
    if (!showModal) {
      setSelectedGround(null);
      setSelectedDate("");
      setSelectedSport("");
      setSelectedField("");
      setSelectedSubfield("");
      setDuration("60");
      setSelectedTimeSlot(null);
      setBookedBy("");
      setPhoneNumber("");
      setBookingAmount("");
      setPaymentType("Advance");
      setDescription("");
      setAvailableSports([]);
      setAvailableFields([]);
      setAvailableSubfields([]);
      setAvailableDurations([]);
      setAvailableTimeSlots([]);
    }
  }, [showModal]);

  // Filter allowed sports from selected ground's fields
  useEffect(() => {
    if (selectedGround && selectedGround.fields) {
      // Extract all unique sport IDs from all fields' allowedSports
      const allowedSportIds = new Set();
      selectedGround.fields.forEach((field) => {
        if (field.allowedSports && Array.isArray(field.allowedSports)) {
          field.allowedSports.forEach((sportId) => {
            allowedSportIds.add(sportId);
          });
        }
      });

      // Filter sportsData to only include allowed sports
      const filteredSports = sportsData.filter((sport) =>
        allowedSportIds.has(sport._id)
      );

      setAvailableSports(filteredSports);
    } else {
      setAvailableSports([]);
    }
  }, [selectedGround]);

  // Filter fields based on selected sport
  useEffect(() => {
    if (selectedGround && selectedGround.fields) {
      let filteredFields = [];
      if (selectedSport) {
        // Filter fields that have the selected sport in their allowedSports
        filteredFields = selectedGround.fields.filter((field) => {
          if (!field.allowedSports || field.allowedSports.length === 0) {
            return true; // Include fields with no sport restrictions
          }
          return field.allowedSports.includes(selectedSport);
        });
      } else {
        // If no sport selected, show all fields
        filteredFields = selectedGround.fields;
      }
      setAvailableFields(filteredFields);
    } else {
      setAvailableFields([]);
    }
  }, [selectedGround, selectedSport]);

  // Reset sport and field when ground changes
  useEffect(() => {
    setSelectedSport("");
    setSelectedField("");
    setSelectedSubfield("");
  }, [selectedGround]);

  // Reset field when sport changes
  useEffect(() => {
    setSelectedField("");
    setSelectedSubfield("");
    setSelectedTimeSlot(null);
    setAvailableTimeSlots([]);
    setAvailableDurations([]);
    setAvailableSubfields([]);
    setDuration("");
  }, [selectedSport]);

  // Extract subfields from selected field
  useEffect(() => {
    if (selectedField && selectedGround && selectedGround.fields) {
      // Find the selected field object
      const field = selectedGround.fields.find((f) => f._id === selectedField);

      if (field && field.subFields && Array.isArray(field.subFields)) {
        // Filter out deleted subfields
        const activeSubfields = field.subFields.filter(
          (subfield) => !subfield.isDeleted
        );
        setAvailableSubfields(activeSubfields);
      } else {
        setAvailableSubfields([]);
      }
    } else {
      setAvailableSubfields([]);
    }
  }, [selectedField, selectedGround]);

  // Reset subfield when field changes
  useEffect(() => {
    setSelectedSubfield("");
  }, [selectedField]);

  // Extract unique durations from selected subfield or field's pricing
  useEffect(() => {
    if (selectedField && selectedGround && selectedGround.fields) {
      // Find the selected field object
      const field = selectedGround.fields.find((f) => f._id === selectedField);

      let pricingArray = [];

      // If subfield is selected, use subfield's pricing, otherwise use field's pricing
      if (selectedSubfield && field && field.subFields) {
        const subfield = field.subFields.find(
          (sf) => sf._id === selectedSubfield
        );
        if (subfield && subfield.pricing && Array.isArray(subfield.pricing)) {
          pricingArray = subfield.pricing;
        }
      } else if (field && field.pricing && Array.isArray(field.pricing)) {
        pricingArray = field.pricing;
      }

      if (pricingArray.length > 0) {
        // Extract unique durations from pricing array
        const durationsSet = new Set();
        pricingArray.forEach((price) => {
          if (price.duration) {
            durationsSet.add(price.duration.toString());
          }
        });

        // Convert to sorted array
        const uniqueDurations = Array.from(durationsSet)
          .map((d) => parseInt(d))
          .sort((a, b) => a - b)
          .map((d) => d.toString());

        setAvailableDurations(uniqueDurations);

        // Set first duration as default if available
        if (uniqueDurations.length > 0 && !duration) {
          setDuration(uniqueDurations[0]);
        }
      } else {
        setAvailableDurations([]);
      }
    } else {
      setAvailableDurations([]);
    }
  }, [duration, selectedField, selectedSubfield, selectedGround]);

  // Reset duration when field or subfield changes
  useEffect(() => {
    setDuration("");
  }, [selectedField, selectedSubfield]);

  // Reset time slot when field, subfield, date, or duration changes
  useEffect(() => {
    setSelectedTimeSlot(null);
    setAvailableTimeSlots([]);
    setBookingAmount("");
  }, [selectedField, selectedSubfield, selectedDate, duration]);

  // Fetch price when time slot is selected
  const fetchPrice = async (slot) => {
    if (
      !selectedGround?._id ||
      !selectedDate ||
      !selectedField ||
      !duration ||
      !slot
    ) {
      return;
    }

    setIsLoadingPrice(true);
    try {
      // Find the field object
      const field = selectedGround.fields.find((f) => f._id === selectedField);

      if (!field) {
        console.error("Field not found");
        return;
      }

      // Find pricing record that matches duration and day of week
      let pricingArray = [];
      if (selectedSubfield && field.subFields) {
        const subfield = field.subFields.find(
          (sf) => sf._id === selectedSubfield
        );
        if (subfield && subfield.pricing) {
          pricingArray = subfield.pricing;
        }
      } else if (field.pricing) {
        pricingArray = field.pricing;
      }

      // Get day of week from selected date
      const dateObj = new Date(selectedDate);
      const dayNames = [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ];
      const dayOfWeek = dayNames[dateObj.getDay()];

      // Find pricing that matches duration and day
      const matchedPricing = pricingArray.find((price) => {
        const priceDurationNumber = Number(price.duration);
        const selectedDurationNumber = Number(duration);

        return (
          !Number.isNaN(priceDurationNumber) &&
          !Number.isNaN(selectedDurationNumber) &&
          priceDurationNumber === selectedDurationNumber &&
          price.StartDays &&
          price.StartDays.includes(dayOfWeek)
        );
      });

      if (!matchedPricing || !matchedPricing._id) {
        console.error("Pricing not found for duration and day");
        setBookingAmount("");
        return;
      }

      // Find timing that matches the date
      let matchedTiming = null;
      if (selectedGround.timing && Array.isArray(selectedGround.timing)) {
        matchedTiming = selectedGround.timing.find((timing) => {
          return timing.StartDays && timing.StartDays.includes(dayOfWeek);
        });
      }

      if (!matchedTiming || !matchedTiming._id) {
        console.error("Timing not found for day");
        setBookingAmount("");
        return;
      }

      // Format date and time for API
      // Convert selectedDate (YYYY-MM-DD) and slot.startTime (e.g., "1:00PM") to ISO format
      const [time, period] = slot.startTime.split(/(AM|PM)/i);
      const [hours, minutes] = time.split(":").map(Number);
      let hour24 = hours;
      if (period.toUpperCase() === "PM" && hours !== 12) {
        hour24 = hours + 12;
      } else if (period.toUpperCase() === "AM" && hours === 12) {
        hour24 = 0;
      }

      const isoDateTimeS = `${selectedDate}T${String(hour24).padStart(
        2,
        "0"
      )}:${String(minutes || 0).padStart(2, "0")}:00`;

      // Build API URL
      let url = `/api/match/get-field-priceIds/${selectedGround._id}?fieldId=${selectedField}&priceId=${matchedPricing._id}&duration=${duration}&startDate=${isoDateTimeS}&timeId=${matchedTiming._id}`;

      // Add subfieldId based on sport type (Football has special logic)
      if (selectedSport === "67319b1621c86e068f6cfec0") {
        // Football sport: check if subfield is selected and different from field
        if (selectedSubfield && field._id !== selectedSubfield) {
          url += `&subFieldId=${selectedSubfield}`;
        }
        // If selectedSubfield exists but field._id === selectedSubfield, don't add subFieldId
      }
      // For other sports, don't add subFieldId

      const response = await axiosInstance.get(url);
      // API response shape:
      // { status: true, data: { priceData: { price: 60, ... } } }
      const apiData = response?.data;
      const priceData = apiData?.data?.priceData;

      if (apiData?.status && priceData?.price != null) {
        setBookingAmount(priceData.price.toString());
      } else {
        setBookingAmount("");
      }
    } catch (error) {
      console.error("Error fetching price:", error);
      setBookingAmount("");
    } finally {
      setIsLoadingPrice(false);
    }
  };

  // Fetch available time slots
  useEffect(() => {
    const fetchAvailableTimeSlots = async () => {
      if (selectedGround?._id && selectedDate && selectedField && duration) {
        setIsLoadingTimeSlots(true);
        try {
          let url = `/api/booking/available-time-slots?groundId=${selectedGround._id}&date=${selectedDate}&fieldId=${selectedField}&duration=${duration}`;

          // Add subfieldId if subfield is selected
          if (selectedSubfield) {
            url += `&subFieldId=${selectedSubfield}`;
          }

          const response = await axiosInstance.get(url);
          console.log("Time slots response:", response?.data);
          setAvailableTimeSlots(response?.data?.data?.slots || []);
        } catch (error) {
          console.error("Error fetching time slots:", error);
          setAvailableTimeSlots([]);
        } finally {
          setIsLoadingTimeSlots(false);
        }
      }
    };

    fetchAvailableTimeSlots();
  }, [axiosInstance, selectedGround, selectedDate, selectedField, selectedSubfield, duration]);

  const handleSubmit = async () => {
    // Validate required fields
    if (
      !selectedGround ||
      !selectedDate ||
      !selectedSport ||
      !selectedField ||
      !selectedTimeSlot ||
      !bookedBy ||
      !phoneNumber ||
      !bookingAmount
    ) {
      alert("Please fill in all required fields and select a time slot");
      return;
    }

    // Prepare booking data
    const bookingData = {
      groundId: selectedGround._id,
      date: selectedDate,
      sportsId: selectedSport,
      fieldId: selectedField,
      startTime: selectedTimeSlot.start,
      endTime: selectedTimeSlot.end,
      duration: duration,
      bookedBy: bookedBy,
      phoneNumber: phoneNumber,
      bookingAmount: parseFloat(bookingAmount),
      paymentType: paymentType.toLowerCase(), // Convert to lowercase (full, advance, on arrival)
      ...(description &&
        description.trim() !== "" && { description: description }),
    };

    // Add subfieldId only if subfield is selected (not empty string)
    if (selectedSubfield && selectedSubfield.trim() !== "") {
      bookingData.subfieldId = selectedSubfield;
    }

    // Call the API through handleSave prop
    if (handleSave) {
      await handleSave(bookingData);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
    });
  };

  return (
    <Dialog
      open={showModal}
      onClose={handleClose}
      maxWidth="lg"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <DialogTitle>Create New Booking</DialogTitle>
      <DialogContent>
        <form>
          {/* Row 1: Select Ground, Select Date, Choose Sport */}
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            <Autocomplete
              options={grounds || []}
              getOptionLabel={(option) => option.name || ""}
              value={selectedGround}
              onChange={(event, newValue) => {
                setSelectedGround(newValue);
                console.log("ground", JSON.stringify(newValue));
              }}
              loading={isLoadingGrounds}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Ground"
                  required
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {isLoadingGrounds ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                  sx={{
                    "& .MuiInput-underline:after": {
                      borderBottomColor: "#0C7240",
                    },
                    margin: "10px",
                    flex: "1 1 30%",
                    minWidth: "200px",
                  }}
                />
              )}
            />

            <TextField
              required
              label="Select Date"
              type="date"
              variant="outlined"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#0C7240",
                },
                margin: "10px",
                flex: "1 1 30%",
                minWidth: "200px",
              }}
            />

            <FormControl
              required
              variant="outlined"
              sx={{
                margin: "10px",
                flex: "1 1 30%",
                minWidth: "200px",
              }}
            >
              <InputLabel id="sport-select-label">Choose Sport</InputLabel>
              <Select
                labelId="sport-select-label"
                label="Choose Sport"
                value={selectedSport}
                onChange={(e) => setSelectedSport(e.target.value)}
                disabled={!selectedGround || availableSports.length === 0}
                sx={{
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "#0C7240",
                  },
                }}
              >
                {availableSports.length > 0 ? (
                  availableSports.map((sport) => (
                    <MenuItem key={sport._id} value={sport._id}>
                      {sport.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    {selectedGround
                      ? "No sports available for this ground"
                      : "Select a ground first"}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </div>

          {/* Row 2: Select Field, Select Subfield (Optional), Duration */}
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            <FormControl
              required
              variant="outlined"
              sx={{
                margin: "10px",
                flex: "1 1 30%",
                minWidth: "200px",
              }}
            >
              <InputLabel id="field-select-label">Select Field</InputLabel>
              <Select
                labelId="field-select-label"
                label="Select Field"
                value={selectedField}
                onChange={(e) => setSelectedField(e.target.value)}
                disabled={
                  !selectedGround ||
                  !selectedSport ||
                  availableFields.length === 0
                }
                sx={{
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "#0C7240",
                  },
                }}
              >
                {availableFields.length > 0 ? (
                  availableFields.map((field) => (
                    <MenuItem key={field._id} value={field._id}>
                      {field.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    {selectedSport
                      ? "No fields available for this sport"
                      : "Select a sport first"}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl
              variant="outlined"
              sx={{
                margin: "10px",
                flex: "1 1 30%",
                minWidth: "200px",
              }}
            >
              <InputLabel id="subfield-select-label">
                Select Subfield (Optional)
              </InputLabel>
              <Select
                labelId="subfield-select-label"
                label="Select Subfield (Optional)"
                value={selectedSubfield}
                onChange={(e) => setSelectedSubfield(e.target.value)}
                disabled={
                  !selectedGround ||
                  !selectedField ||
                  availableSubfields.length === 0
                }
                sx={{
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "#0C7240",
                  },
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {availableSubfields.length > 0 ? (
                  availableSubfields.map((subfield) => (
                    <MenuItem key={subfield._id} value={subfield._id}>
                      {subfield.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    {selectedField
                      ? "No subfields available for this field"
                      : "Select a field first"}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControl
              required
              variant="outlined"
              sx={{
                margin: "10px",
                flex: "1 1 30%",
                minWidth: "200px",
              }}
            >
              <InputLabel id="duration-select-label">
                Duration (minutes)
              </InputLabel>
              <Select
                labelId="duration-select-label"
                label="Duration (minutes)"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                disabled={
                  !selectedGround ||
                  !selectedField ||
                  availableDurations.length === 0
                }
                sx={{
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "#0C7240",
                  },
                }}
              >
                {availableDurations.length > 0 ? (
                  availableDurations.map((dur) => (
                    <MenuItem key={dur} value={dur}>
                      {dur} minutes
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    {selectedField
                      ? "No durations available"
                      : "Select a field first"}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </div>

          {/* Available Time Slots Section */}
          {selectedGround && selectedDate && selectedField && duration && (
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#000",
                  mb: 1,
                  fontWeight: "bold",
                }}
              >
                Available Time Slots
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#000",
                  mb: 2,
                }}
              >
                {formatDate(selectedDate)}
              </Typography>
              {isLoadingTimeSlots ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : availableTimeSlots.length > 0 ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 2,
                    p: 2,
                  }}
                >
                  {availableTimeSlots.map((slot, index) => (
                    <Button
                      key={index}
                      variant={
                        selectedTimeSlot?.start === slot.start
                          ? "contained"
                          : "outlined"
                      }
                      onClick={async () => {
                        setSelectedTimeSlot(slot);
                        await fetchPrice(slot);
                      }}
                      sx={{
                        backgroundColor:
                          selectedTimeSlot?.start === slot.start
                            ? "#0C7240"
                            : "#fff",
                        color:
                          selectedTimeSlot?.start === slot.start
                            ? "#fff"
                            : "#000",
                        borderColor:
                          selectedTimeSlot?.start === slot.start
                            ? "#0C7240"
                            : "rgba(0, 0, 0, 0.23)",
                        borderRadius: 2,
                        py: 1.5,
                        "&:hover": {
                          backgroundColor:
                            selectedTimeSlot?.start === slot.start
                              ? "#0C7240"
                              : "#f5f5f5",
                          borderColor:
                            selectedTimeSlot?.start === slot.start
                              ? "#0C7240"
                              : "rgba(0, 0, 0, 0.23)",
                        },
                      }}
                    >
                      {slot.startTime} - {slot.endTime}
                    </Button>
                  ))}
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: "#000",
                    p: 2,
                    backgroundColor: "#f5f5f5",
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  No time slots available for this date and duration
                </Typography>
              )}
            </Box>
          )}

          {/* Row 3: Booked By, Booking by Phone Number, Booking Amount */}
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            <TextField
              required
              label="Booked By"
              type="text"
              variant="outlined"
              value={bookedBy}
              onChange={(e) => setBookedBy(e.target.value)}
              placeholder="Enter name"
              sx={{
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#0C7240",
                },
                margin: "10px",
                flex: "1 1 30%",
                minWidth: "200px",
              }}
            />

            <TextField
              required
              label="Booking by Phone Number"
              type="tel"
              variant="outlined"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="e.g., +923001234567"
              sx={{
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#0C7240",
                },
                margin: "10px",
                flex: "1 1 30%",
                minWidth: "200px",
              }}
            />

            <TextField
              required
              label="Booking Amount"
              type="number"
              variant="outlined"
              value={bookingAmount}
              disabled={true}
              // InputProps={{
              //   endAdornment: isLoadingPrice ? (
              //     <CircularProgress size={20} />
              //   ) : null,
              // }}
              sx={{
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#0C7240",
                },
                margin: "10px",
                flex: "1 1 30%",
                minWidth: "200px",
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#000",
                },
              }}
            />
          </div>

          {/* Row 4: Payment Type */}
          <div style={{ display: "flex", flexWrap: "wrap", marginTop: "10px" }}>
            <Box sx={{ margin: "10px", flex: "1 1 100%" }}>
              <Typography
                variant="body1"
                sx={{
                  color: "#000",
                  mb: 1,
                  fontWeight: "500",
                }}
              >
                Payment Type
              </Typography>
              <RadioGroup
                row
                value={paymentType}
                onChange={(e) => setPaymentType(e.target.value)}
                sx={{
                  "& .MuiRadio-root": {
                    color: "#0C7240",
                  },
                  "& .MuiRadio-root.Mui-checked": {
                    color: "#0C7240",
                  },
                }}
              >
                <FormControlLabel
                  value="Advance"
                  control={<Radio />}
                  label="Advance"
                />
                <FormControlLabel
                  value="Full"
                  control={<Radio />}
                  label="Full"
                />
                <FormControlLabel
                  value="On arrival"
                  control={<Radio />}
                  label="On arrival"
                />
              </RadioGroup>
            </Box>
          </div>

          {/* Row 5: Description */}
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            <TextField
              label="Description"
              type="text"
              variant="outlined"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description (optional)"
              multiline
              rows={3}
              sx={{
                "& .MuiInput-underline:after": {
                  borderBottomColor: "#0C7240",
                },
                margin: "10px",
                flex: "1 1 100%",
                minWidth: "200px",
              }}
            />
          </div>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} sx={{ color: "#666" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            backgroundColor: "#0C7240",
            "&:hover": {
              backgroundColor: "#0C7240",
            },
          }}
        >
          Create Booking
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateBookingModal;
